/**
 * Parking — Charger install requests (/parking/charger-requests) — V0.3 Tier 1.
 *
 * Owner-submitted EV-charger install requests. Admin approves / rejects with
 * decision notes. Cancelled state is set by the requesting owner.
 *
 * Backend contracts:
 *   GET   /v1/parking/charger-requests?status=…&cursor&limit
 *   POST  /v1/parking/charger-requests/:id/approve   body { decision_notes }
 *   POST  /v1/parking/charger-requests/:id/reject    body { decision_notes }
 *   POST  /v1/parking/charger-requests/:id/cancel
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Wrench,
  CheckCircle2,
  XCircle,
  CalendarDays,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate, formatDateTime } from '@/lib/format';
import type {
  ChargerInstallRequest,
  ChargerRequestStatus,
  CursorList
} from './_parking-types';
import { REQUEST_STATUS_BADGE } from './_parking-types';

const STATUS_LABEL: Record<ChargerRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled'
};

const DecisionSchema = z.object({
  decision_notes: z.string().trim().min(3, 'Notes required').max(1000)
});
type DecisionForm = z.infer<typeof DecisionSchema>;

export function ParkingChargerRequestsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ChargerRequestStatus | 'all'>('pending');

  const filtersHash = useMemo(() => JSON.stringify([tab]), [tab]);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQ = useQuery({
    queryKey: ['parking-charger-requests', tab, cursor],
    queryFn: () =>
      get<CursorList<ChargerInstallRequest>>('/parking/charger-requests', {
        params: {
          limit: 50,
          ...(tab !== 'all' ? { status: tab } : {}),
          ...(cursor ? { cursor } : {})
        }
      })
  });
  const paginator = useCursorAccumulator<ChargerInstallRequest>(listQ.data, cursor, filtersHash);
  const requests = paginator.items;

  const [decisionTarget, setDecisionTarget] = useState<{
    r: ChargerInstallRequest;
    kind: 'approve' | 'reject';
  } | null>(null);
  const decisionForm = useForm<DecisionForm>({
    resolver: zodResolver(DecisionSchema),
    mode: 'onChange',
    defaultValues: { decision_notes: '' }
  });
  useEffect(() => {
    if (decisionTarget) decisionForm.reset({ decision_notes: '' });
  }, [decisionTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = useMutation({
    mutationFn: (v: DecisionForm) => {
      if (!decisionTarget) throw new Error('No request');
      const path =
        decisionTarget.kind === 'approve'
          ? `/parking/charger-requests/${decisionTarget.r.id}/approve`
          : `/parking/charger-requests/${decisionTarget.r.id}/reject`;
      return post<ChargerInstallRequest>(path, { decision_notes: v.decision_notes });
    },
    onSuccess: () => {
      toast.success(decisionTarget?.kind === 'approve' ? 'Request approved' : 'Request rejected');
      setDecisionTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-charger-requests'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <>
      <PageHeader
        title="EV charger install requests"
        description="Owner-submitted requests to install a private EV charger."
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/parking/chargers' })} title="Back to chargers">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as ChargerRequestStatus | 'all')}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'cancelled', label: 'Cancelled' },
                { value: 'all', label: 'All' }
              ]}
              aria-label="Request status filter"
            />
          </CardContent>
        </Card>

        {listQ.isLoading ? (
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Could not load requests"
                description={getErrorMessage(listQ.error)}
              />
            </CardContent>
          </Card>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Wrench className="h-6 w-6" />}
                title="No requests in this view"
                description="Owner-submitted EV-charger install requests will show here."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            {requests.map((r) => (
              <Card key={r.id}>
                <CardContent className="flex flex-col gap-vt-3 p-vt-4">
                  <div className="flex items-start justify-between gap-vt-3">
                    <div className="min-w-0">
                      <div className="text-vt-12 text-vt-text-muted">
                        Requested by{' '}
                        <span className="text-vt-text">
                          {r.requesting_unit
                            ? `${r.requesting_unit.block?.name ? r.requesting_unit.block.name + ' · ' : ''}${r.requesting_unit.number}`
                            : '—'}
                        </span>
                      </div>
                      <h4 className="mt-vt-1 text-vt-14 font-semibold text-vt-text">
                        {r.brand ?? 'Charger'} · {r.power_kw ?? '—'} kW
                      </h4>
                      <div className="mt-vt-1 flex flex-wrap items-center gap-vt-2 text-vt-12 text-vt-text-muted">
                        {r.connector_type ? <span>Connector: {r.connector_type}</span> : null}
                        {r.slot ? <span>· Slot {r.slot.slot_number}</span> : null}
                        {r.proposed_date ? (
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            {formatDate(r.proposed_date)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <Badge variant={REQUEST_STATUS_BADGE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </div>
                  {r.notes ? (
                    <p className="text-vt-12 text-vt-text-muted">{r.notes}</p>
                  ) : null}
                  {r.decision_notes ? (
                    <div className="rounded-vt-sm border border-vt-border bg-vt-surface-2/60 p-vt-2 text-vt-12 text-vt-text-muted">
                      <span className="font-semibold text-vt-text">Decision: </span>
                      {r.decision_notes}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-center justify-between gap-vt-2 border-t border-vt-border pt-vt-2">
                    <span className="text-vt-11 text-vt-text-muted">
                      {formatDateTime(r.created_at)}
                    </span>
                    {r.status === 'pending' ? (
                      <div className="flex items-center gap-vt-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setDecisionTarget({ r, kind: 'reject' })}
                        >
                          <XCircle className="h-4 w-4" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setDecisionTarget({ r, kind: 'approve' })}
                        >
                          <CheckCircle2 className="h-4 w-4" /> Approve
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <CursorPaginator
          hasMore={Boolean(paginator.nextCursor)}
          loading={listQ.isFetching}
          itemsShown={requests.length}
          pageSize={50}
          onLoadMore={() => {
            if (paginator.nextCursor) setCursor(paginator.nextCursor);
          }}
        />
      </div>

      <Dialog
        open={Boolean(decisionTarget)}
        onOpenChange={(v) => (v ? null : setDecisionTarget(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionTarget?.kind === 'approve' ? 'Approve request' : 'Reject request'}
            </DialogTitle>
            <DialogDescription>
              {decisionTarget?.kind === 'approve'
                ? 'Add an internal note (visible in audit log).'
                : 'Tell the owner why this request was rejected.'}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={decisionForm.handleSubmit((v) => decide.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="dr-notes">Decision notes</Label>
              <Textarea
                id="dr-notes"
                rows={4}
                placeholder={
                  decisionTarget?.kind === 'approve'
                    ? 'e.g. Slot wired up by 25th; coordinate with electrician.'
                    : 'e.g. Slot has no 3-phase wiring; relocate to B2.'
                }
                {...decisionForm.register('decision_notes')}
              />
              {decisionForm.formState.errors.decision_notes ? (
                <p className="text-vt-12 text-vt-alert">
                  {decisionForm.formState.errors.decision_notes.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDecisionTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant={decisionTarget?.kind === 'approve' ? 'primary' : 'secondary'}
                loading={decide.isPending}
                loadingText="Saving…"
                disabled={decide.isPending || !decisionForm.formState.isValid}
              >
                {decisionTarget?.kind === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
